import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_OPTIONS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function buildYearOptions(selectedYear) {
  const current = new Date().getFullYear();
  const center = Number.isFinite(selectedYear) ? selectedYear : current;
  const years = new Set();
  for (let y = current - 15; y <= current + 2; y += 1) years.add(y);
  for (let y = center - 5; y <= center + 5; y += 1) {
    if (y >= 2000 && y <= 2100) years.add(y);
  }
  return Array.from(years).sort((a, b) => b - a);
}

function toIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseIsoDate(value) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function formatDisplayDate(value) {
  const date = parseIsoDate(value);
  if (!date) return '';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, count) {
  return new Date(date.getFullYear(), date.getMonth() + count, 1);
}

function sameDay(a, b) {
  return (
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isBetween(day, from, to) {
  if (!from || !to) return false;
  const t = day.getTime();
  const a = Math.min(from.getTime(), to.getTime());
  const b = Math.max(from.getTime(), to.getTime());
  return t > a && t < b;
}

function buildMonthDays(viewMonth) {
  const first = startOfMonth(viewMonth);
  const startPad = first.getDay();
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(first.getFullYear(), first.getMonth(), day));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/**
 * Single-field date range picker: first click = From, second click = To.
 */
export function DateRangePicker({
  id = 'date-range',
  label = 'Date range',
  dateFrom = '',
  dateTo = '',
  onChange,
  disabled = false,
  placeholder = 'Select date range',
  hint = '',
}) {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    const from = parseIsoDate(dateFrom);
    return startOfMonth(from || new Date());
  });
  const [draftFrom, setDraftFrom] = useState(dateFrom);
  const [draftTo, setDraftTo] = useState(dateTo);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 288 });
  const rootRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setDraftFrom(dateFrom);
      setDraftTo(dateTo);
      const from = parseIsoDate(dateFrom);
      if (from) setViewMonth(startOfMonth(from));
    }
  }, [dateFrom, dateTo, open]);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return undefined;

    const updatePosition = () => {
      const rect = buttonRef.current.getBoundingClientRect();
      const width = Math.max(rect.width, 288);
      const estimatedHeight = 340;
      const spaceBelow = window.innerHeight - rect.bottom;
      const top =
        spaceBelow < estimatedHeight && rect.top > estimatedHeight
          ? rect.top - estimatedHeight - 6
          : rect.bottom + 6;
      let left = rect.left;
      if (left + width > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - width - 8);
      }
      setMenuPos({ top, left, width });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointer = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (buttonRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  const days = useMemo(() => buildMonthDays(viewMonth), [viewMonth]);
  const fromDate = parseIsoDate(draftFrom);
  const toDate = parseIsoDate(draftTo);
  const yearOptions = useMemo(
    () => buildYearOptions(viewMonth.getFullYear()),
    [viewMonth]
  );

  const displayValue = (() => {
    if (dateFrom && dateTo) {
      return `${formatDisplayDate(dateFrom)} – ${formatDisplayDate(dateTo)}`;
    }
    if (dateFrom) return `${formatDisplayDate(dateFrom)} – …`;
    if (dateTo) return `… – ${formatDisplayDate(dateTo)}`;
    return '';
  })();

  const handleDayClick = (day) => {
    if (disabled || !day) return;
    const iso = toIsoDate(day);

    if (!draftFrom || (draftFrom && draftTo)) {
      setDraftFrom(iso);
      setDraftTo('');
      return;
    }

    let nextFrom = draftFrom;
    let nextTo = iso;
    if (iso < draftFrom) {
      nextFrom = iso;
      nextTo = draftFrom;
    }
    setDraftFrom(nextFrom);
    setDraftTo(nextTo);
    onChange?.({ dateFrom: nextFrom, dateTo: nextTo });
    setOpen(false);
  };

  const handleClear = () => {
    setDraftFrom('');
    setDraftTo('');
    onChange?.({ dateFrom: '', dateTo: '' });
    setOpen(false);
  };

  const setViewMonthNumber = (monthIndex) => {
    setViewMonth((prev) => new Date(prev.getFullYear(), monthIndex, 1));
  };

  const setViewYearNumber = (year) => {
    setViewMonth((prev) => new Date(year, prev.getMonth(), 1));
  };

  const pickingHint = !draftFrom
    ? 'Select start date'
    : !draftTo
      ? 'Select end date'
      : `${formatDisplayDate(draftFrom)} – ${formatDisplayDate(draftTo)}`;

  const selectClass =
    'h-7 rounded-md bg-black border border-gray-700 text-xs text-white px-1.5 focus:outline-none focus:ring-1 focus:ring-gray-600';

  return (
    <div className="min-w-0">
      <label htmlFor={id} className="block text-sm font-medium text-gray-300 mb-1 leading-5">
        {label}
      </label>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        className={`w-full h-9 box-border px-3 text-sm text-left bg-black border border-gray-700 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-600 disabled:opacity-50 disabled:cursor-not-allowed ${
          displayValue ? 'text-white' : 'text-gray-500'
        }`}
      >
        {displayValue || placeholder}
      </button>
      {hint ? <p className="text-[10px] text-gray-500 mt-0.5 leading-tight">{hint}</p> : null}
      {(dateFrom || dateTo) && !disabled ? (
        <button
          type="button"
          onClick={handleClear}
          className="mt-0.5 text-[10px] text-amber-200/80 hover:text-amber-200 underline"
        >
          Clear dates
        </button>
      ) : null}

      {open
        ? createPortal(
            <div
              ref={rootRef}
              className="z-[80] rounded-lg border border-gray-700 bg-gray-950 shadow-xl p-3"
              style={{
                position: 'fixed',
                top: menuPos.top,
                left: menuPos.left,
                width: menuPos.width,
              }}
              role="dialog"
              aria-label="Choose date range"
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => setViewMonth((m) => addMonths(m, -1))}
                  className="h-7 w-7 rounded-md text-gray-300 hover:bg-gray-800 hover:text-white shrink-0"
                  aria-label="Previous month"
                >
                  ‹
                </button>
                <div className="flex items-center gap-1.5 min-w-0 flex-1 justify-center">
                  <select
                    aria-label="Month"
                    value={viewMonth.getMonth()}
                    onChange={(e) => setViewMonthNumber(Number(e.target.value))}
                    className={`${selectClass} min-w-0 flex-1`}
                  >
                    {MONTH_OPTIONS.map((name, index) => (
                      <option key={name} value={index}>
                        {name}
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label="Year"
                    value={viewMonth.getFullYear()}
                    onChange={(e) => setViewYearNumber(Number(e.target.value))}
                    className={`${selectClass} w-[4.5rem] shrink-0`}
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => setViewMonth((m) => addMonths(m, 1))}
                  className="h-7 w-7 rounded-md text-gray-300 hover:bg-gray-800 hover:text-white shrink-0"
                  aria-label="Next month"
                >
                  ›
                </button>
              </div>

              <p className="text-[11px] text-gray-400 mb-2 text-center">{pickingHint}</p>

              <div className="grid grid-cols-7 gap-0.5 mb-1">
                {WEEKDAYS.map((d) => (
                  <div
                    key={d}
                    className="h-7 flex items-center justify-center text-[10px] font-semibold text-gray-500"
                  >
                    {d}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-0.5">
                {days.map((day, idx) => {
                  if (!day) {
                    return <div key={`empty-${idx}`} className="h-8" />;
                  }
                  const iso = toIsoDate(day);
                  const isFrom = sameDay(day, fromDate);
                  const isTo = sameDay(day, toDate);
                  const inRange = isBetween(day, fromDate, toDate);
                  const isEndpoint = isFrom || isTo;
                  return (
                    <button
                      key={iso}
                      type="button"
                      onClick={() => handleDayClick(day)}
                      className={`h-8 rounded-md text-xs transition-colors ${
                        isEndpoint
                          ? 'bg-amber-400 text-black font-semibold'
                          : inRange
                            ? 'bg-amber-400/20 text-amber-100'
                            : 'text-gray-200 hover:bg-gray-800'
                      }`}
                    >
                      {day.getDate()}
                    </button>
                  );
                })}
              </div>

              <div className="mt-3 flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-[11px] text-gray-400 hover:text-white underline"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-[11px] text-amber-200/80 hover:text-amber-200"
                >
                  Close
                </button>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
